import { Upload } from 'lucide-react'
import { Fragment, useCallback, useState, type ChangeEvent, type DragEvent } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { type Dayjs } from 'dayjs'
import { useOnboarding } from '../../context/OnboardingContext'
import { MOCK_SL_ADMIN } from '../../data/mockSeed'
import type { OtrTransactionRow } from '../../types/onboarding'
import { MISC_PRIMARY, MISC_PRIMARY_HOVER } from '../../theme/onboardingTheme'

function emptyDraftFromRow(row: OtrTransactionRow): OtrTransactionRow {
  return { ...row }
}

const correctionFieldSx = {
  mt: 1,
  width: '100%',
  '& .MuiOutlinedInput-root': {
    fontSize: 12,
    '& fieldset': { borderColor: '#cbd5e1' },
  },
  '& .MuiOutlinedInput-input': {
    py: 0.75,
    px: 1,
  },
}

export function StepOtr() {
  const { state, dispatch } = useOnboarding()
  const [dragActive, setDragActive] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<OtrTransactionRow | null>(null)

  const triggerUpload = useCallback(() => {
    dispatch({ type: 'UPLOAD_OTR_REPORT' })
  }, [dispatch])

  const onFileInput = useCallback(
    (files: FileList | null) => {
      if (!files?.length) return
      triggerUpload()
    },
    [triggerUpload],
  )

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault()
      setDragActive(false)
      onFileInput(e.dataTransfer.files)
    },
    [onFileInput],
  )

  const openEdit = (row: OtrTransactionRow) => {
    if (!row.flaggedForReview) return
    setEditingId(row.id)
    setDraft(emptyDraftFromRow(row))
  }

  const closeEdit = () => {
    setEditingId(null)
    setDraft(null)
  }

  const submitCorrection = () => {
    if (!draft || !editingId) return
    dispatch({
      type: 'UPDATE_OTR_ROW',
      id: editingId,
      patch: {
        invoiceNumber: draft.invoiceNumber.trim(),
        insuredName: draft.insuredName.trim(),
        policyNumber: draft.policyNumber.trim(),
        homeState: draft.homeState.trim().toUpperCase(),
        grossPremium: Number.isFinite(draft.grossPremium) ? draft.grossPremium : 0,
        taxAmt: Number.isFinite(draft.taxAmt) ? draft.taxAmt : 0,
        carrier: draft.carrier.trim(),
        flaggedForReview: false,
      },
    })
    dispatch({ type: 'TOAST', message: 'Correction submitted. Thank you.' })
    closeEdit()
  }

  const mailApiHref = `mailto:${MOCK_SL_ADMIN.email}?subject=${encodeURIComponent('AMS API connection request')}`

  return (
    <Stack sx={{ minWidth: 0 }} spacing={4}>
      <Paper
        component="section"
        variant="outlined"
        sx={{
          borderColor: '#e2e8f0',
          borderRadius: '12px',
          backgroundColor: '#ffffff',
          p: 2,
          boxShadow: 1,
        }}
      >
        <Stack spacing={2}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
            Transaction report
          </Typography>
          <Box
            onDragEnter={(e) => {
              e.preventDefault()
              setDragActive(true)
            }}
            onDragLeave={(e) => {
              e.preventDefault()
              if (e.currentTarget === e.target) setDragActive(false)
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            sx={{
              borderRadius: '12px',
              border: '2px dashed',
              px: 3,
              py: 5,
              textAlign: 'center',
              transition: 'border-color 150ms, background-color 150ms',
              ...(dragActive
                ? {
                    borderColor: MISC_PRIMARY,
                    backgroundColor: 'rgba(239, 246, 255, 0.6)',
                  }
                : {
                    borderColor: '#cbd5e1',
                    backgroundColor: '#f8fafc',
                    '&:hover': { borderColor: '#94a3b8' },
                  }),
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Upload size={40} color="#94a3b8" aria-hidden />
            </Box>
            <Typography sx={{ mt: 1.5, fontSize: 14, fontWeight: 500, color: '#0f172a' }}>
              Upload your transaction report — export directly from your AMS (Applied Epic, HawkSoft,
              etc.) as a CSV or XLSX.
            </Typography>
            <Typography sx={{ mt: 1, fontSize: 12, color: '#475569' }}>
              Drag and drop your file here, or choose a file from your computer.
            </Typography>
            <Button
              component="label"
              variant="contained"
              sx={{
                mt: 2.5,
                backgroundColor: MISC_PRIMARY,
                px: 2.5,
                py: 1.25,
                fontSize: 12,
                fontWeight: 600,
                color: '#ffffff',
                boxShadow: 1,
                '&:hover': { backgroundColor: MISC_PRIMARY_HOVER },
              }}
            >
              <Box
                component="input"
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                sx={{ display: 'none' }}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onFileInput(e.target.files)}
              />
              Choose file
            </Button>
            {state.otrReportUploaded ? (
              <Typography sx={{ mt: 2, fontSize: 14, fontWeight: 500, color: '#065f46' }}>
                Report received successfully.
              </Typography>
            ) : null}
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
              Transition date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                inputFormat="MM/DD/YYYY"
                value={state.otrTransitionDate ? dayjs(state.otrTransitionDate) : null}
                onChange={(value: Dayjs | null) =>
                  dispatch({
                    type: 'SET_OTR_DATE',
                    date: value && value.isValid() ? value.format('YYYY-MM-DD') : '',
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="MM/DD/YYYY"
                    sx={{
                      mt: 1,
                      display: 'block',
                      width: '100%',
                      maxWidth: 320,
                      '& .MuiOutlinedInput-root': {
                        fontSize: 14,
                        '& fieldset': { borderColor: '#cbd5e1' },
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Box>
        </Stack>
      </Paper>

      <Paper
        component="section"
        variant="outlined"
        sx={{
          borderColor: '#e2e8f0',
          borderRadius: '12px',
          backgroundColor: '#ffffff',
          p: 2.5,
          boxShadow: 1,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
          Direct API connection
        </Typography>
        <Typography sx={{ mt: 1, fontSize: 14, color: '#475569' }}>
          Interested in a direct API connection from your AMS? Contact us to get set up.
        </Typography>
        <Button
          href={mailApiHref}
          variant="contained"
          sx={{
            mt: 2,
            display: 'inline-flex',
            backgroundColor: MISC_PRIMARY,
            px: 2,
            py: 1,
            fontSize: 12,
            fontWeight: 600,
            color: '#ffffff',
            '&:hover': { backgroundColor: MISC_PRIMARY_HOVER },
          }}
        >
          Email MISC
        </Button>
      </Paper>

      <Paper
        component="section"
        variant="outlined"
        sx={{
          borderColor: '#e2e8f0',
          borderRadius: '12px',
          backgroundColor: '#ffffff',
          p: 2,
          boxShadow: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
            Weekly transaction preview
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>Week 2026-W17 (demo)</Typography>
        </Box>
        <Typography sx={{ mt: 0.5, fontSize: 12, color: '#475569' }}>
          Preview of how your report will appear after processing. Low-confidence rows are flagged —
          click <Box component="span" sx={{ fontWeight: 600 }}>Flagged</Box> to correct details.
        </Typography>
        <Box sx={{ mt: 2, overflowX: 'auto' }}>
          <Table sx={{ minWidth: 640 }} size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ px: 1, py: 1, fontSize: 12, color: '#64748b' }}>Invoice</TableCell>
                <TableCell sx={{ px: 1, py: 1, fontSize: 12, color: '#64748b' }}>Insured</TableCell>
                <TableCell sx={{ px: 1, py: 1, fontSize: 12, color: '#64748b' }}>Policy</TableCell>
                <TableCell sx={{ px: 1, py: 1, fontSize: 12, color: '#64748b' }}>State</TableCell>
                <TableCell sx={{ px: 1, py: 1, fontSize: 12, color: '#64748b' }}>Premium</TableCell>
                <TableCell sx={{ px: 1, py: 1, fontSize: 12, color: '#64748b' }}>Tax</TableCell>
                <TableCell sx={{ px: 1, py: 1, fontSize: 12, color: '#64748b' }}>Review</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.otrRows.map((t) => (
                <Fragment key={t.id}>
                  <TableRow sx={{ '& td': { borderTop: '1px solid #f1f5f9' } }}>
                    <TableCell sx={{ px: 1, py: 1, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                      {t.invoiceNumber}
                    </TableCell>
                    <TableCell sx={{ px: 1, py: 1, fontSize: 12 }}>{t.insuredName}</TableCell>
                    <TableCell sx={{ px: 1, py: 1, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                      {t.policyNumber}
                    </TableCell>
                    <TableCell sx={{ px: 1, py: 1, fontSize: 12 }}>{t.homeState}</TableCell>
                    <TableCell sx={{ px: 1, py: 1, fontSize: 12 }}>
                      ${t.grossPremium.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ px: 1, py: 1, fontSize: 12 }}>
                      ${t.taxAmt.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ px: 1, py: 1, fontSize: 12 }}>
                      {t.flaggedForReview ? (
                        <Button
                          type="button"
                          onClick={() => openEdit(t)}
                          sx={{
                            minWidth: 0,
                            borderRadius: '4px',
                            px: 0.75,
                            py: 0.25,
                            fontSize: 12,
                            fontWeight: 500,
                            transition: 'background-color 150ms, box-shadow 150ms',
                            ...(editingId === t.id
                              ? {
                                  backgroundColor: '#fde68a',
                                  color: '#451a03',
                                  boxShadow: `0 0 0 2px #fbbf24`,
                                }
                              : {
                                  backgroundColor: '#fef3c7',
                                  color: '#78350f',
                                  '&:hover': { backgroundColor: '#fde68a' },
                                }),
                          }}
                        >
                          Flagged
                        </Button>
                      ) : (
                        <Typography component="span" sx={{ fontSize: 12, color: '#94a3b8' }}>
                          OK
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  {editingId === t.id && draft ? (
                    <TableRow
                      key={`${t.id}-edit`}
                      sx={{ backgroundColor: 'rgba(255, 251, 235, 0.5)', '& td': { borderTop: '1px solid #fef3c7' } }}
                    >
                      <TableCell colSpan={7} sx={{ px: 1.5, py: 2 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#451a03' }}>
                          Correct transaction
                        </Typography>
                        <Box
                          sx={{
                            mt: 1.5,
                            display: 'grid',
                            gap: 1.5,
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
                          }}
                        >
                          <Box
                            component="label"
                            sx={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#334155' }}
                          >
                            Invoice
                            <TextField
                              size="small"
                              fullWidth
                              value={draft.invoiceNumber}
                              onChange={(e) => setDraft({ ...draft, invoiceNumber: e.target.value })}
                              sx={correctionFieldSx}
                            />
                          </Box>
                          <Box
                            component="label"
                            sx={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#334155' }}
                          >
                            Insured
                            <TextField
                              size="small"
                              fullWidth
                              value={draft.insuredName}
                              onChange={(e) => setDraft({ ...draft, insuredName: e.target.value })}
                              sx={correctionFieldSx}
                            />
                          </Box>
                          <Box
                            component="label"
                            sx={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#334155' }}
                          >
                            Policy
                            <TextField
                              size="small"
                              fullWidth
                              value={draft.policyNumber}
                              onChange={(e) => setDraft({ ...draft, policyNumber: e.target.value })}
                              sx={correctionFieldSx}
                            />
                          </Box>
                          <Box
                            component="label"
                            sx={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#334155' }}
                          >
                            State
                            <TextField
                              size="small"
                              fullWidth
                              value={draft.homeState}
                              onChange={(e) => setDraft({ ...draft, homeState: e.target.value })}
                              sx={correctionFieldSx}
                            />
                          </Box>
                          <Box
                            component="label"
                            sx={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#334155' }}
                          >
                            Premium ($)
                            <TextField
                              type="number"
                              size="small"
                              fullWidth
                              value={draft.grossPremium}
                              onChange={(e) =>
                                setDraft({
                                  ...draft,
                                  grossPremium: Number.parseFloat(e.target.value) || 0,
                                })
                              }
                              sx={correctionFieldSx}
                            />
                          </Box>
                          <Box
                            component="label"
                            sx={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#334155' }}
                          >
                            Tax ($)
                            <TextField
                              type="number"
                              size="small"
                              fullWidth
                              value={draft.taxAmt}
                              onChange={(e) =>
                                setDraft({ ...draft, taxAmt: Number.parseFloat(e.target.value) || 0 })
                              }
                              sx={correctionFieldSx}
                            />
                          </Box>
                          <Box
                            component="label"
                            sx={{
                              display: 'block',
                              fontSize: 11,
                              fontWeight: 500,
                              color: '#334155',
                              gridColumn: { sm: '1 / -1', lg: '1 / -1' },
                            }}
                          >
                            Carrier
                            <TextField
                              size="small"
                              fullWidth
                              value={draft.carrier}
                              onChange={(e) => setDraft({ ...draft, carrier: e.target.value })}
                              sx={correctionFieldSx}
                            />
                          </Box>
                        </Box>
                        <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ mt: 2 }}>
                          <Button
                            type="button"
                            variant="contained"
                            onClick={submitCorrection}
                            sx={{
                              backgroundColor: MISC_PRIMARY,
                              px: 2,
                              py: 1,
                              fontSize: 12,
                              fontWeight: 600,
                              color: '#ffffff',
                              '&:hover': { backgroundColor: MISC_PRIMARY_HOVER },
                            }}
                          >
                            Submit correction
                          </Button>
                          <Button
                            type="button"
                            variant="outlined"
                            onClick={closeEdit}
                            sx={{
                              borderColor: '#cbd5e1',
                              px: 2,
                              py: 1,
                              fontSize: 12,
                              fontWeight: 600,
                              color: '#334155',
                              '&:hover': { backgroundColor: '#f8fafc' },
                            }}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box component="section" sx={{ mt: 3, borderTop: '1px solid #f1f5f9', pt: 2.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
            Reconciliation Acknowledgment
          </Typography>
          <Typography sx={{ mt: 1, fontSize: 14, color: '#475569' }}>
            Please upload the transaction report weekly. This ensures that we can provide timely and
            accurate filings.
          </Typography>
          <Box
            component="label"
            sx={{
              mt: 1.5,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              fontSize: 14,
              color: '#1e293b',
            }}
          >
            <Checkbox
              checked={state.otrReconciliationAck}
              onChange={() => dispatch({ type: 'ACK_RECONCILIATION' })}
              sx={{ mt: -0.5, p: 0.5 }}
            />
            <Box component="span">
              By checking this box, you agree to provide weekly transaction reports.
            </Box>
          </Box>
        </Box>

        <Stack
          direction="row"
          flexWrap="wrap"
          spacing={1}
          sx={{ mt: 2.5, borderTop: '1px solid #f1f5f9', pt: 2.5 }}
        >
          <Button
            type="button"
            variant="contained"
            onClick={() => dispatch({ type: 'ATTEST_WEEKLY' })}
            disabled={state.weeklyAttested}
            sx={{
              backgroundColor: '#059669',
              px: 2,
              py: 1,
              fontSize: 12,
              fontWeight: 600,
              color: '#ffffff',
              '&:hover': { backgroundColor: '#047857' },
              '&.Mui-disabled': { opacity: 0.5, color: '#ffffff' },
            }}
          >
            Confirm — nothing missing
          </Button>
          <Button
            type="button"
            variant="outlined"
            onClick={() =>
              dispatch({ type: 'TOAST', message: 'Add Missing Transaction form would open (demo).' })
            }
            sx={{
              borderColor: '#cbd5e1',
              px: 2,
              py: 1,
              fontSize: 12,
              fontWeight: 600,
              color: '#334155',
              '&:hover': { backgroundColor: '#f8fafc' },
            }}
          >
            Add missing transaction
          </Button>
        </Stack>
        {state.weeklyAttested ? (
          <Typography sx={{ mt: 1, fontSize: 12, color: '#065f46' }}>
            Attestation logged with timestamp (demo).
          </Typography>
        ) : null}
      </Paper>
    </Stack>
  )
}
